import { action, makeObservable, observable, reaction } from 'mobx';
import {
    RaptorComponentsApiClient,
    RaptorComponentVersionsApiClient,
    RaptorDockerImagesApiClient,
    RaptorManifestPlatformsApiClient,
} from 'api';
import { AssureAdminApiClientStore } from './assure-admin-api-client.store';
import { AssureBaseStore, IAssureStoreConstructorOptions } from './assure-base.store';
import {
    ComponentDto,
    ComponentMessageResponseDto,
    CreateComponentRequest,
    DeleteComponentQueryRequest,
    ListComponentsQueryRequest,
    ListComponentVersionsQueryRequest,
    UpdateComponentRequest,
} from 'dto/raptor-component.dto';
import {
    ComponentVersionDto,
    ComponentVersionMessageResponseDto,
    CreateComponentVersionRequest,
    ManifestPlatformDto,
    UpdateComponentVersionRequest,
} from 'dto/raptor-component-version.dto';
import { DockerImageDto, ListDockerImagesQueryRequest } from 'dto/raptor-docker-image.dto';

export type IRaptorComponentsStoreConstructorOptions = IAssureStoreConstructorOptions;

export class RaptorComponentsStore extends AssureBaseStore<RaptorComponentsApiClient, ComponentDto> {
    private readonly _apiClientStore: AssureAdminApiClientStore;

    @observable
    public manifestPlatforms: ManifestPlatformDto[] = [];

    public constructor(options: IRaptorComponentsStoreConstructorOptions) {
        super(options);
        makeObservable(this);
        this._apiClientStore = options.apiClientStore;
        reaction(
            () => options.authStore.assureApiAccount,
            () => {
                (async () => {
                    if (!options.authStore.assureApiAccount?.accountId) return;
                    await this.listManifestPlatforms(options.authStore.assureApiAccount.accountId);
                })();
            },
            { fireImmediately: true },
        );
    }

    protected get apiClient(): RaptorComponentsApiClient {
        return this._apiClientStore.apiClients.raptorComponents;
    }

    protected get componentVersionsApiClient(): RaptorComponentVersionsApiClient {
        return this._apiClientStore.apiClients.raptorComponentVersions;
    }

    protected get manifestPlatformsApiClient(): RaptorManifestPlatformsApiClient {
        return this._apiClientStore.apiClients.raptorManifestPlatforms;
    }

    protected get dockerImagesApiClient(): RaptorDockerImagesApiClient {
        return this._apiClientStore.apiClients.raptorDockerImages;
    }

    public async createComponent(
        accountId: number,
        accountUuid: string,
        params: CreateComponentRequest,
    ): Promise<ComponentDto> {
        return this.apiClient.createComponent(accountId, accountUuid, params);
    }

    public async getComponents(accountId: number, query?: ListComponentsQueryRequest): Promise<ComponentDto[]> {
        return this.apiClient.getComponents(accountId, query);
    }

    public async getComponent(accountId: number, uuid: string): Promise<ComponentDto> {
        return this.apiClient.getComponent(accountId, uuid);
    }

    public async updateComponent(
        accountId: number,
        uuid: string,
        params: UpdateComponentRequest,
    ): Promise<ComponentDto> {
        return this.apiClient.updateComponent(accountId, uuid, params);
    }

    public async deleteComponent(
        accountId: number,
        uuid: string,
        query?: DeleteComponentQueryRequest,
    ): Promise<ComponentMessageResponseDto> {
        return this.apiClient.deleteComponent(accountId, uuid, query);
    }

    public async listComponentVersions(
        accountId: number,
        componentUuid: string,
        query?: ListComponentVersionsQueryRequest,
    ): Promise<ComponentVersionDto[]> {
        return this.apiClient.listComponentVersions(accountId, componentUuid, query);
    }

    public async createComponentVersion(
        accountId: number,
        componentUuid: string,
        params: CreateComponentVersionRequest,
    ): Promise<ComponentVersionDto> {
        return this.componentVersionsApiClient.createComponentVersion(accountId, componentUuid, params);
    }

    public async getComponentVersion(accountId: number, uuid: string): Promise<ComponentVersionDto> {
        return this.componentVersionsApiClient.getComponentVersion(accountId, uuid);
    }

    public async updateComponentVersion(
        accountId: number,
        uuid: string,
        params: UpdateComponentVersionRequest,
    ): Promise<ComponentVersionDto> {
        return this.componentVersionsApiClient.updateComponentVersion(accountId, uuid, params);
    }

    public async deleteComponentVersion(
        accountId: number,
        uuid: string,
    ): Promise<ComponentVersionMessageResponseDto> {
        return this.componentVersionsApiClient.deleteComponentVersion(accountId, uuid);
    }

    public async publishComponentVersion(
        accountId: number,
        uuid: string,
    ): Promise<ComponentVersionMessageResponseDto> {
        return this.componentVersionsApiClient.publishComponentVersion(accountId, uuid);
    }

    public getManifestPlatformDisplayName(platformUuid: string): string {
        const platform = this.manifestPlatforms.find((item) => item.uuid === platformUuid);
        return platform?.displayName;
    }

    @action
    public setManifestPlatforms(manifestPlatforms: ManifestPlatformDto[]): void {
        this.manifestPlatforms = manifestPlatforms;
    }

    public async listManifestPlatforms(accountId: number, forceRefresh = false): Promise<ManifestPlatformDto[]> {
        if (!forceRefresh && this.manifestPlatforms.length > 0) {
            return this.manifestPlatforms;
        }

        const manifestPlatforms = await this.manifestPlatformsApiClient.listManifestPlatforms(accountId);
        this.setManifestPlatforms(manifestPlatforms);
        return manifestPlatforms;
    }

    public async listDockerImages(accountId: number, accountUuid: string, paginationQuery?: Pick<ListDockerImagesQueryRequest, 'offset' | 'limit'>): Promise<DockerImageDto[]> {
        const dockerImages: DockerImageDto[] = await this.dockerImagesApiClient.listDockerImages(accountId, {
            accountUuid,
            includeSharedDockerImages: true,
            offset: paginationQuery?.offset,
            limit: paginationQuery?.limit,
        });
        return dockerImages;
    }
}
